import React, { useState } from 'react';
import { ClipboardCheckIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon, BookOpenIcon, InfoIcon } from 'lucide-react';
export const HealthAssessment = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const questions = [{
    question: 'How often do you experience recurring vaginal health issues?',
    options: ['Rarely or never', 'Occasionally (1-3 times per year)', 'Frequently (4+ times per year)']
  }, {
    question: 'Have conventional treatments provided lasting relief?',
    options: ["Haven't needed treatments", 'Yes, treatments were effective', 'Only temporary relief']
  }, {
    question: 'Which symptom would you like to learn more about?',
    options: ['Vaginal dryness', 'Recurring infections/BV', 'Odor or discharge', 'Discomfort during intimacy']
  }];
  const handleAnswer = (answer: string) => {
    const newAnswers = [...answers];
    newAnswers[currentStep] = answer;
    setAnswers(newAnswers);
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResults(true);
    }
  };
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers([]);
    setShowResults(false);
  };
  // Determine educational resources based on answers
  const getEducationalResources = () => {
    // Frequent issues + treatments not working + any symptom = biofilm information
    if (answers[0] === 'Frequently (4+ times per year)' && answers[1] === 'Only temporary relief') {
      return {
        title: "Understanding Biofilm's Role in Recurring Issues",
        description: 'Research suggests that bacterial biofilms (protective shields) may be preventing treatments from working effectively. Learn about this breakthrough understanding.',
        link: '/adv-1',
        linkText: 'Read the biofilm research',
        resources: ["The Science of Bacterial Biofilms in Women's Health", 'Why Antibiotics Often Fail: The Biofilm Connection', 'Enzyme Therapies: Breaking Through Bacterial Shields']
      };
    }
    // Vaginal dryness as main concern = Menopause
    else if (answers[2] === 'Vaginal dryness' || answers[2] === 'Discomfort during intimacy') {
      return {
        title: 'The Science of Vaginal Tissue Health',
        description: 'Hormonal changes can affect vaginal tissues in complex ways. Understanding these mechanisms can help you make informed decisions about management approaches.',
        link: '/adv-1',
        linkText: "Explore women's health science",
        resources: ['Hormonal Influences on Vaginal Tissue Elasticity', 'Beyond Estrogen: Comprehensive Approaches to Vaginal Health', 'Clinical Research on Vaginal Dryness Management']
      };
    }
    // Default recommendation
    else {
      return {
        title: 'Evidence-Based Vaginal Health Information',
        description: 'Understanding the microbiome and how it affects vaginal health can help you make informed decisions about preventative care.',
        link: '/adv-1',
        linkText: 'Explore our research',
        resources: ['The Vaginal Microbiome: A Delicate Balance', 'pH Balance and Vaginal Health: What the Research Shows', 'Prevention Strategies Based on Clinical Evidence']
      };
    }
  };
  const educationalResources = getEducationalResources();
  return <section className="my-12" data-id="element-2448">
      <h2 className="text-2xl font-serif font-bold text-slate-900 mt-10 mb-5 leading-tight border-b border-slate-200 pb-2 flex items-center" data-id="element-2449">
        <ClipboardCheckIcon className="h-5 w-5 text-slate-700 mr-2 inline" data-id="element-2450" />
        Women's Health Knowledge Assessment
      </h2>
      <div className="bg-white rounded-lg border border-slate-200 p-6" data-id="element-2451">
        {!showResults ? <>
            <div className="mb-6" data-id="element-2452">
              <div className="flex justify-between items-center mb-2" data-id="element-2453">
                <span className="text-sm font-medium text-slate-700" data-id="element-2454">
                  Question {currentStep + 1} of {questions.length}
                </span>
                <div className="flex space-x-1" data-id="element-2455">
                  {questions.map((_, index) => <div key={index} className={`w-8 h-1 rounded-full ${index === currentStep ? 'bg-indigo-500' : index < currentStep ? 'bg-slate-300' : 'bg-slate-200'}`} data-id="element-2456" />)}
                </div>
              </div>
              <h3 className="text-lg font-serif font-semibold text-slate-900" data-id="element-2457">
                {questions[currentStep].question}
              </h3>
            </div>
            <div className="space-y-3" data-id="element-2458">
              {questions[currentStep].options.map((option, index) => <button key={index} className={`w-full text-left p-3 rounded-md border ${answers[currentStep] === option ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'} transition-colors duration-200`} onClick={() => handleAnswer(option)} data-id="element-2459">
                  <div className="flex items-center" data-id="element-2460">
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${answers[currentStep] === option ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`} data-id="element-2461">
                      {answers[currentStep] === option && <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-2462" />}
                    </div>
                    <span data-id="element-2463">{option}</span>
                  </div>
                </button>)}
            </div>
            {currentStep > 0 && <div className="mt-6 flex justify-start" data-id="element-2464">
                <button className="inline-flex items-center text-slate-600 hover:text-slate-900" onClick={handlePrevious} data-id="element-2465">
                  <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-2466" />
                  Previous question
                </button>
              </div>}
          </> : <div data-id="element-2467">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2468">
              <BookOpenIcon className="h-8 w-8 text-indigo-600" data-id="element-2469" />
            </div>
            <h3 className="text-xl font-serif font-semibold text-slate-900 mb-2 text-center" data-id="element-2470">
              Educational Resources For You
            </h3>
            <p className="text-slate-600 text-center mb-6" data-id="element-2471">
              Based on your responses, here are some evidence-based resources
              that may be helpful:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-6 mt-4" data-id="element-2472">
              <h4 className="font-medium text-slate-900 mb-2" data-id="element-2473">
                {educationalResources.title}
              </h4>
              <p className="text-slate-700 mb-4" data-id="element-2474">
                {educationalResources.description}
              </p>
              <div className="bg-white p-4 rounded border border-slate-200 mb-4" data-id="element-2475">
                <h5 className="font-medium text-slate-800 flex items-center mb-3" data-id="element-2476">
                  <InfoIcon className="h-4 w-4 text-indigo-500 mr-2" data-id="element-2477" />
                  Recommended Reading
                </h5>
                <ul className="space-y-2" data-id="element-2478">
                  {educationalResources.resources.map((resource, index) => <li key={index} className="flex items-start" data-id="element-2479">
                      <span className="text-indigo-500 mr-2" data-id="element-2480">•</span>
                      <span className="text-slate-700" data-id="element-2481">{resource}</span>
                    </li>)}
                </ul>
              </div>
              <a href={educationalResources.link} className="inline-flex items-center bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors" data-id="element-2482">
                {educationalResources.linkText}
                <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-2483" />
              </a>
            </div>
            <div className="text-center" data-id="element-2484">
              <button className="text-indigo-600 hover:text-indigo-800 mt-2" onClick={resetQuiz} data-id="element-2485">
                Take the assessment again
              </button>
            </div>
          </div>}
      </div>
      <div className="mt-4 text-xs text-slate-500 italic" data-id="element-2486">
        Note: This assessment is for educational purposes only and is not a
        substitute for professional medical advice. Please consult with a
        healthcare provider for personalized recommendations.
      </div>
    </section>;
};